import React, { useMemo } from 'react'
import { indexBy, pipe, prop } from 'lodash/fp'
import { AlertTriangleIcon, InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	getSalesEntitiesTree,
	selectionFromMapping,
	toLegacyLeafIds,
	useOptimizationGroupQuery,
	useProductGroupCatalogQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import { useMainScenarioQuery } from '@/entities/scenario'
import { VideoTutorials } from '@/entities/video-tutorial'

import { OptimizationGroupActions } from '@/widgets/og-actions'
import { OptimizationGroupActionsDropdown } from '@/widgets/og-actions-dropdown'
import { LocalPricingCampaigns } from '@/widgets/pricing-campaigns-table'
import { BusinessSettingsView } from '@/widgets/scenario-business'
import { StopListView } from '@/widgets/scenario-stoplist'
import { StrategyView } from '@/widgets/scenario-strategy'

import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import { PageNavigatorRegister } from '@/shared/components/PageNavigator'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content } from '@/shared/components/ui/layout'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import { VideoTag } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { useModals } from '@/shared/lib/modal-manager'
import { findNodes, getChildrenIds } from '@/shared/lib/tree'

import { LockedForChangesNotification } from './LockedForChangesNotification'

export default function OptimizationGroupSettingsPage() {
	const { t } = useTranslation()

	return (
		<Content className='overflow-y-auto'>
			<PageNavigatorRegister>
				<div className='flex items-center gap-1'>
					<OptimizationGroupActions />
					<VideoTutorials tags={[VideoTag.OptimizationGroupSettings]}>
						<Button size='icon' className='ml-auto' variant='ghost'>
							<InfoIcon />
						</Button>
					</VideoTutorials>
				</div>
			</PageNavigatorRegister>
			<div className='w-full py-4'>
				<div className='flex flex-col px-4'>
					<LockedForChangesNotification className='mb-2' />
					<div className='mb-4 flex items-center justify-between'>
						<h1 className='text-lg font-bold'>
							{t('og.settings', {
								defaultValue: 'Optimization Group settings',
							})}
						</h1>
						<OptimizationGroupActionsDropdown />
					</div>

					<div className='mb-8 grid w-full grid-cols-6 gap-4'>
						<OptimizationGroupStructureCard />
						<OptimizationGroupStrateryCard />
						<OptimizationGroupBusinessSettingsCard />
						<OptimizationGroupStopListCard />
					</div>
				</div>
				<h1 className='px-4 text-lg font-bold'>
					{t('general_pc', { defaultValue: 'Pricing campaigns' })}
				</h1>
				<LocalPricingCampaigns />
			</div>
		</Content>
	)
}

function OptimizationGroupStructureCard() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const optimizationGroupId = useOptimizationGroupId()!
	const optimizationGroupQuery = useOptimizationGroupQuery(optimizationGroupId)
	const salesEntitiesQuery = useSalesEntitiesQuery()
	const productGroupsQuery = useProductGroupCatalogQuery()
	const salesEntitiesTree = useMemo(
		() => getSalesEntitiesTree(salesEntitiesQuery.data),
		[salesEntitiesQuery.data]
	)
	const isLoading =
		optimizationGroupQuery.isLoading ||
		salesEntitiesQuery.isLoading ||
		productGroupsQuery.isLoading
	const { sales_entities, product_groups } = useMemo(() => {
		if (!optimizationGroupQuery.data || !salesEntitiesTree) {
			return {
				sales_entities: [],
				product_groups: [],
			}
		}
		const { sales_entities, product_groups } = optimizationGroupQuery.data

		return {
			sales_entities: getChildrenIds(
				findNodes(salesEntitiesTree, sales_entities)
			),
			product_groups: productGroupsQuery.data
				? toLegacyLeafIds(
						productGroupsQuery.data.pickerForest,
						selectionFromMapping(
							productGroupsQuery.data.pickerForest,
							product_groups
						)
					)
				: [],
		}
	}, [optimizationGroupQuery.data, salesEntitiesTree, productGroupsQuery.data])
	const sales_level = useMemo(() => {
		if (!optimizationGroupQuery.data || !salesEntitiesQuery.data) {
			return undefined
		}
		const salesLevelsDict = pipe([
			prop('sales_levels'),
			indexBy(prop('level')),
		])(salesEntitiesQuery.data)

		return salesLevelsDict?.[optimizationGroupQuery.data.sales_level_id]
	}, [salesEntitiesQuery.data, optimizationGroupQuery.data])

	return (
		<Card
			data-testid='og-structure-card'
			className='group/card relative cursor-pointer border hover:border-primary'
			onClick={() =>
				showModal('OPTIMIZATION_GROUP_MODAL', {
					optimizationGroupId,
					initialTabId: 'structure',
				})
			}
		>
			<CardHeader>
				<CardTitle>
					{t('og.structure', { defaultValue: 'OG structure' })}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<DescriptionList>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('levels', { defaultValue: 'Levels' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{isLoading ? <Spinner /> : <strong>{sales_level?.name}</strong>}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('sales_units', { defaultValue: 'Sales units' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{isLoading ? (
								<Spinner />
							) : (
								<strong>{sales_entities?.length}</strong>
							)}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('product_groups', { defaultValue: 'Product groups' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{isLoading ? (
								<Spinner />
							) : (
								<strong>{product_groups?.length}</strong>
							)}
						</DescriptionDetail>
					</DescriptionGroup>
				</DescriptionList>
			</CardContent>
		</Card>
	)
}
function OptimizationGroupStrateryCard() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const optimizationGroupId = useOptimizationGroupId()!
	const settingsQuery = useMainScenarioQuery(optimizationGroupId)
	const isLoading = settingsQuery.isLoading
	if (isLoading) {
		return <Skeleton className='h-full w-full' />
	}

	return (
		<Card
			className='group/card relative h-full cursor-pointer border fade-in hover:border-primary'
			onClick={() =>
				showModal('OPTIMIZATION_GROUP_MODAL', {
					optimizationGroupId,
					initialTabId: 'strategy',
				})
			}
		>
			<CardHeader>
				<CardTitle>
					{t('pricing_strategy', { defaultValue: 'Pricing Strategy' })}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<ErrorBoundaryWithUser fallback={CardError}>
					<StrategyView data={settingsQuery.data?.settings.strategy} />
				</ErrorBoundaryWithUser>
			</CardContent>
		</Card>
	)
}

function OptimizationGroupBusinessSettingsCard() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const optimizationGroupId = useOptimizationGroupId()!
	const settingsQuery = useMainScenarioQuery(optimizationGroupId)
	const isLoading = settingsQuery.isLoading
	if (isLoading) {
		return <Skeleton className='col-span-2 h-full w-full' />
	}

	return (
		<Card
			className='group/card relative col-span-2 h-full cursor-pointer border fade-in hover:border-primary'
			onClick={() =>
				showModal('OPTIMIZATION_GROUP_MODAL', {
					optimizationGroupId,
					initialTabId: 'business',
				})
			}
		>
			<CardHeader>
				<CardTitle>
					{t('og.settings.business', { defaultValue: 'Business constrains' })}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<ErrorBoundaryWithUser fallback={CardError}>
					<BusinessSettingsView
						settings={settingsQuery.data?.settings.business}
						compact
					/>
				</ErrorBoundaryWithUser>
			</CardContent>
		</Card>
	)
}

function OptimizationGroupStopListCard() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const optimizationGroupId = useOptimizationGroupId()!
	const settingsQuery = useMainScenarioQuery(optimizationGroupId)
	const isLoading = settingsQuery.isLoading
	if (isLoading) {
		return <Skeleton className='col-span-2 h-full w-full' />
	}

	return (
		<Card
			className='group/card relative col-span-2 h-full cursor-pointer border fade-in hover:border-primary'
			onClick={() =>
				showModal('OPTIMIZATION_GROUP_MODAL', {
					optimizationGroupId,
					initialTabId: 'stop_list',
				})
			}
		>
			<CardHeader>
				<CardTitle>
					{t('repricing_stop_list', { defaultValue: 'Repricing stop list' })}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<ErrorBoundaryWithUser showDialog fallback={CardError}>
					<StopListView
						settings={settingsQuery.data?.settings.stop_list}
						compact
					/>
				</ErrorBoundaryWithUser>
			</CardContent>
		</Card>
	)
}
const CardError = (props: { resetError?: () => void }) => {
	const { t } = useTranslation()

	return (
		<div className='my-2 flex flex-auto items-center justify-center'>
			<Empty className='md:p-0'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<AlertTriangleIcon />
					</EmptyMedia>
					<EmptyTitle>{t('fatal_error_title')}🐞</EmptyTitle>
					<EmptyDescription>{t('fatal_error_desc')}</EmptyDescription>
				</EmptyHeader>
				<EmptyContent>
					{!!props.resetError && (
						<Button
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								props.resetError?.()
							}}
						>
							Retry
						</Button>
					)}
				</EmptyContent>
			</Empty>
		</div>
	)
}
